import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { AvisoDeAcesso, TelaDaArkipel, temAcesso } from '@/components/AcessoDoCliente';
import { PLANOS, precoDoPlano, creditosDoPlano } from '@/lib/planos';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PLANS = [
  {
    id: 'harbor',
    name: PLANOS.harbor.nome,
    price: precoDoPlano('harbor'),
    credits: creditosDoPlano('harbor'),
    features: ['Geração de imagens', 'Copy com IA', 'Kanban editorial', 'Publicação automática'],
  },
  {
    id: 'voyage',
    name: PLANOS.voyage.nome,
    price: precoDoPlano('voyage'),
    credits: creditosDoPlano('voyage'),
    features: ['Tudo do Harbor', 'Maior volume de conteúdo', 'Suporte prioritário', 'Múltiplas marcas'],
    highlight: true,
  },
];

export function PlanGate({ children }) {
  const { user, account, refreshAccount, logout } = useAuth();
  const [refreshed, setRefreshed] = useState(false);
  const [checkingOut, setCheckingOut] = useState(null);

  const isSuperAdmin = user?.role === 'superadmin';
  const entraGuardado = temAcesso(account);

  // Todos os hooks ANTES de qualquer return condicional (regra do React)
  useEffect(() => {
    if (isSuperAdmin) {
      setRefreshed(true);
      return undefined;
    }
    // O estado de acesso é relido a cada abertura (PROMPT 201): suspensão, vencimento e pagamento registrado no
    // Backoffice valem sem novo login. Quem já entrava vê a tela na hora, enquanto a releitura acontece.
    refreshAccount().finally(() => setRefreshed(true));
    // Uma rota que responde 403 de acesso relê o estado (a tela troca sem recarregar).
    const reler = () => { refreshAccount(); };
    window.addEventListener('acesso_mudou', reler);
    return () => window.removeEventListener('acesso_mudou', reler);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Returns condicionais APÓS todos os hooks ─────────────────────────────────

  // Superadmin sempre passa — verificado após hooks
  if (isSuperAdmin) return children;

  // Aguarda o refresh inicial (só quem ainda não entrava; quem entrava segue na tela)
  if (!refreshed && !entraGuardado) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="animate-spin h-8 w-8 border-4 border-accent border-t-transparent rounded-full" />
      </div>
    );
  }

  // Acesso liberado (assinatura, pagamento por transferência, cortesia ou carência), com o aviso perto de vencer
  if (entraGuardado) {
    return (
      <>
        {children}
        <AvisoDeAcesso acesso={account?.acesso} />
      </>
    );
  }

  // Transferência, cortesia vencida ou suspensa: fale com a Arkipel, nunca o Stripe
  if (account?.acesso?.tela === 'arkipel') return <TelaDaArkipel acesso={account.acesso} onSair={logout} />;

  // Sem assinatura — exibe seleção de planos
  const handleSelectPlan = async (planId) => {
    setCheckingOut(planId);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      // 1. Criar marca padrão (não requer assinatura ativa)
      const brandRes = await axios.post(
        `${API}/brands`,
        { name: 'Minha Marca', segment: 'Geral' },
        { headers }
      );
      const brandId = brandRes.data?.id;
      if (!brandId) throw new Error('Erro ao criar marca');

      // 2. Iniciar checkout Stripe
      const checkoutRes = await axios.post(
        `${API}/billing/checkout`,
        { brand_id: brandId, plan: planId, origin_url: window.location.origin },
        { headers }
      );
      const url = checkoutRes.data?.url || checkoutRes.data?.checkout_url;
      if (url) {
        window.location.href = url;
      } else {
        throw new Error('URL de checkout não encontrada');
      }
    } catch (err) {
      console.error('Checkout error:', err);
      toast.error('Erro ao iniciar checkout. Tente novamente.');
      setCheckingOut(null);
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col items-center justify-center px-4 py-16">
      <div className="max-w-3xl w-full text-center mb-12">
        <h1 className="text-4xl font-heading font-semibold text-foreground mb-3">Escolha seu plano</h1>
        <p className="text-muted-foreground text-base">
          Selecione o plano ideal para sua marca e comece a criar conteúdo com IA.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl w-full">
        {PLANS.map((plan) => (
          <div
            key={plan.id}
            className={`relative rounded-2xl border p-8 flex flex-col gap-4 transition-all ${
              plan.highlight
                ? 'border-accent bg-accent/5 shadow-lg shadow-accent/10'
                : 'border-border bg-card'
            }`}
          >
            {plan.highlight && (
              <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-accent text-accent-foreground text-xs font-semibold px-3 py-1 rounded-full">
                Mais popular
              </span>
            )}

            <div>
              <h2 className="text-2xl font-bold text-foreground">{plan.name}</h2>
              <p className="text-3xl font-extrabold text-foreground mt-1">{plan.price}</p>
              <p className="text-sm text-muted-foreground mt-1">{plan.credits}</p>
            </div>

            <ul className="flex flex-col gap-2 flex-1">
              {plan.features.map((f) => (
                <li key={f} className="flex items-center gap-2 text-sm text-muted-foreground">
                  <span className="text-accent">✓</span>
                  {f}
                </li>
              ))}
            </ul>

            <button
              onClick={() => handleSelectPlan(plan.id)}
              disabled={!!checkingOut}
              data-testid={`select-plan-${plan.id}`}
              className={`w-full py-3 rounded-xl font-semibold text-sm transition-all ${
                plan.highlight
                  ? 'bg-accent text-accent-foreground hover:opacity-90'
                  : 'bg-muted text-foreground hover:bg-muted/80'
              } disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              {checkingOut === plan.id ? 'Redirecionando...' : `Assinar ${plan.name}`}
            </button>
          </div>
        ))}
      </div>

      <p className="text-xs text-muted-foreground mt-10">
        Pagamento seguro via Stripe · Cancele a qualquer momento
      </p>
    </div>
  );
}
